import { ExclamationCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { Dropdown, Menu, Modal, Tooltip, Typography, message } from 'antd';
import cx from 'classnames';
import { FC, MouseEvent, memo, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import PlanogramService from '../../../../../api/planogram/services';
import { useAuth } from '../../../../../hooks/useAuth';
import { PlanogramModel } from '../../../../../models/planogram';
import { useAppDispatch } from '../../../../../store';
import { deletePlanogram } from '../../../../../store/features/planogram/planogramSlice';
import { UserPermissions, formatDate } from '../../../../../util';
import { isBetaSite } from '../../../../../util/environment';
import { DrawerHashRoute } from '../../../../containers/Drawers/types';
import './PlanogramProjectItem.less';

const { Text, Title } = Typography;

interface PlanogramProjectItemProps {
  project: PlanogramModel;
  onItemClick?: (projectId: string) => void;
}

const PlanogramProjectItem: FC<PlanogramProjectItemProps> = ({
  project,
  onItemClick,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isSendingToOVR, setIsSendingToOVR] = useState<boolean>(false);
  const { hasPermission } = useAuth();
  const dispatch = useAppDispatch();

  const handleDeleteProject = useCallback(async () => {
    try {
      await dispatch(deletePlanogram(project.uuid)).unwrap();
      message.success(`Project '${project.name}' deleted successfully`);
    } catch (error) {
      message.error('Failed to delete project');
    }
  }, [dispatch, project.uuid, project.name]);

  const handleSendToOVR = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (isBetaSite()) {
        return;
      }

      try {
        setIsSendingToOVR(true);
        await PlanogramService().sendToOVR(project.uuid);
        message.success(`Project '${project.name}' sent to OVR successfully`);
      } catch (error) {
        message.error('Failed to send project to OVR');
        console.error('Error sending to OVR:', error);
      } finally {
        setIsSendingToOVR(false);
      }
    },
    [project]
  );

  const handleDownloadProject = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        setIsDownloading(true);
        let mediaFiles = project.media_files;

        if (!mediaFiles || mediaFiles.length === 0) {
          const detailedProject = await PlanogramService().getPlanogram(
            project.uuid
          );
          mediaFiles = detailedProject.data.media_files;
        }

        if (mediaFiles && mediaFiles.length > 0) {
          const mediaFile = mediaFiles[mediaFiles.length - 1];
          window.open(mediaFile.media_url, '_blank');
          message.success(`Downloading project: ${project?.name}`);
        } else {
          message.warning('No media files available for download');
        }
      } catch (error) {
        message.error('Failed to fetch project details');
      } finally {
        setIsDownloading(false);
      }
    },
    [project]
  );

  const handleConfirmDeleteProject = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      Modal.confirm({
        title: `Delete project '${project?.name}'?`,
        icon: <ExclamationCircleOutlined />,
        content: 'You will not be able to recover it.',
        okText: 'Delete',
        onOk: handleDeleteProject,
      });
    },
    [handleDeleteProject, project?.name]
  );

  const handleCardClick = (e: MouseEvent) => {
    if (onItemClick) {
      e.preventDefault();
      onItemClick(project.uuid);
    }
  };

  const moreMenuIconClass = useMemo(
    () => cx('more-menu-icon', { active: isMenuOpen }),
    [isMenuOpen]
  );

  const menu = useMemo(
    () => (
      <Menu onClick={() => setIsMenuOpen(false)}>
        <Menu.Item key="1" disabled={isDownloading}>
          <div onClick={handleDownloadProject}>
            {isDownloading ? 'Downloading...' : 'Download'}
          </div>
        </Menu.Item>
        <Menu.Item key="2" disabled={isSendingToOVR || isBetaSite()}>
          <div
            onClick={handleSendToOVR}
            style={isBetaSite() ? { color: '#999' } : undefined}
          >
            {isSendingToOVR ? 'Sending to OVR...' : 'Send to OVR'}
          </div>
        </Menu.Item>
        {hasPermission(UserPermissions.PlanogramProjectsEdit) && (
          <Menu.Item key="3">
            <Link
              to={{
                hash: DrawerHashRoute.PlanogramForm,
                state: { data: project },
              }}
              onClick={(e) => e.stopPropagation()}
            >
              Edit
            </Link>
          </Menu.Item>
        )}
        {hasPermission(UserPermissions.PlanogramProjectsDelete) && (
          <Menu.Item key="4">
            <div onClick={handleConfirmDeleteProject}>Delete</div>
          </Menu.Item>
        )}
      </Menu>
    ),
    [
      handleConfirmDeleteProject,
      handleDownloadProject,
      handleSendToOVR,
      hasPermission,
      project,
      isDownloading,
      isSendingToOVR,
    ]
  );

  const cardContent = (
    <div className="planogram-grid-item" onClick={handleCardClick}>
      <div className="planogram-grid-item-section-top">
        <div className="planogram-grid-item-top-section-left">
          <div className="planogram-item-title">
            <Title level={5} ellipsis>
              {project.name}
            </Title>
          </div>
          <div className="planogram-item-subtitle">
            {project.description ? (
              <Tooltip title={project.description} mouseEnterDelay={1}>
                <Text style={{ width: '100%' }} ellipsis type="secondary">
                  {project.description}
                </Text>
              </Tooltip>
            ) : (
              <Text type="secondary" style={{ fontStyle: 'italic' }}>
                No description.
              </Text>
            )}
          </div>
        </div>
        <div className="planogram-grid-item-top-section-right">
          <Dropdown
            overlay={menu}
            trigger={['click']}
            visible={isMenuOpen}
            onVisibleChange={(value) => setIsMenuOpen(value)}
          >
            <Tooltip title="More" mouseEnterDelay={1}>
              <MoreOutlined
                className={moreMenuIconClass}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Dropdown>
        </div>
      </div>
      <div className="planogram-grid-item-section-bottom">
        <div className="planogram-grid-item-detail-wrapper">
          <Text strong>Created By</Text>
          <Text>{project.createdByUser?.name || 'Unknown'}</Text>
        </div>
        <div className="planogram-grid-item-detail-wrapper">
          <Text strong>Last Updated</Text>
          <Text>{formatDate(project.updated_at)}</Text>
        </div>
      </div>
    </div>
  );

  return onItemClick ? (
    cardContent
  ) : (
    <Link to={`/planogram/${project.uuid}`}>{cardContent}</Link>
  );
};

export default memo(PlanogramProjectItem);
